import React, {useEffect, useState, useMemo, useCallback} from "react";
import "./App.css";
import {Route, Switch, useHistory} from "react-router-dom";
import Routes from "../routing/Routes";
import {
    CssBaseline,
    ThemeProvider,
    Box,
    Logout,
    MainLoader,
    setAlert,
    darkTheme,
    theme,
    AuthActions,
    AuthGState,
    Footer,
    Navbar,
    getAllOrgsIdsForUserByServiceName,
    useFavoriteTheme,
    ADTE_LOGO_SMALL_URL,
} from "@leap4/frontend";
import {useLocation} from "react-router-dom";
import {ConnectedProps, useDispatch} from "react-redux";
import {connect} from "react-redux";
import {
    BACKEND_SERVICE_NAME,
    logoutUrl,
    meMultiUrl,
    requiredAuthRoles,
} from "../../config/generals";
import {RootState} from "../../interfaces/root-state.interface";
import {urlService} from "../routing/urlsService";
import {QueryClient, QueryClientProvider} from "react-query";
import {setIsLoading} from "../../store/actions/dateRange.actions";
import {emptyUserView, UserViewsContext} from "../../contexts/userViewsContext";


const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;


interface IProps extends PropsFromRedux {
    history: History,
    auth: AuthGState
}

const queryClient = new QueryClient();

const styles = {
    root: {
        display: "flex",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        minHeight: { xs: 78, md: 78 },
    },
    content: {
        flexGrow: 1,
        width: "100%",
        margin: "auto",
    },
};

const App = ({auth, setAlert}: IProps) => {
    const history = useHistory();
    const [selectedTheme, setSelectedTheme] = useFavoriteTheme()
    const location = useLocation();
    const dispatch = useDispatch();
    const [bIsSidebarOpen, SetSideBarOpenState] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [currentUserId, setCurrentUserId] = useState('')
    const [userViews, setUserViews] = useState({
        userViewsPresets: [emptyUserView],
        activePreset: emptyUserView
    })
    const nSidebarWidth = 240;

    const handleThemeChange = useCallback((checked: boolean) => {
        const themeToShow = checked ? darkTheme : theme;
        setSelectedTheme(themeToShow)
    }, [setSelectedTheme]);
    useEffect(() => {
        (async () => {
                try {
                    await AuthActions.loadUser(dispatch, BACKEND_SERVICE_NAME, meMultiUrl)
                } finally {
                    setIsLoading(false)
                }
            }
        )();
    }, []);

    useEffect(()=>{
        if(auth.user){
            if(auth.user._id !== currentUserId) setCurrentUserId(auth.user._id)
        }
    },[auth.user?._id, currentUserId])

    const navbarLinks = useMemo(() => [
        {text: "Organizations", link: urlService.organizations()},
        {text: "Advertiser Logins", link: urlService.advertiserLogins()},
    ], []);

    const userOrganizations = useMemo(() => {
        if (!auth.user?.organizations) return [];
        return getAllOrgsIdsForUserByServiceName(auth.user, auth.user.organizations as string[], requiredAuthRoles, BACKEND_SERVICE_NAME);
    }, [auth.user?._id, auth.user?.organizations]);

    const handleDrawerClose = useCallback(() => SetSideBarOpenState(false), []);
    const handleDrawerOpen = useCallback(() => SetSideBarOpenState(true), []);

    const logoutAction = useCallback(async () => {
        await AuthActions.logout(dispatch, history, logoutUrl);
    }, [dispatch, history]);

    const logoutObj = useMemo(() => ({
        text: "Logout",
        action: logoutAction,
        icon: <Logout/>
    }), [logoutAction]);

    const navbarAuthProp = useMemo(() => ({isAuthenticated: !!auth.user}), [auth.user]);
    const navbarUiProp = useMemo(() => ({nSidebarWidth, nNavbarHeight: 78}), [nSidebarWidth]);

    if (isLoading) {
        return <MainLoader/>
    }


    return (
        <>
            <ThemeProvider theme={selectedTheme}>
                <QueryClientProvider client={queryClient}>
                    <UserViewsContext.Provider value={{userViews, setUserViews}}>
                    <Box sx={styles.root}>
                        <CssBaseline/>

                        {auth?.user && <Navbar
                            bIsSidebarOpen={bIsSidebarOpen}
                            handleDrawerClose={handleDrawerClose}
                            handleDrawerOpen={handleDrawerOpen}
                            auth={navbarAuthProp}
                            ui={navbarUiProp}
                            location={location.pathname}
                            links={navbarLinks}
                            orgsWithPermssions={userOrganizations}
                            updateCheckedToFather={handleThemeChange}
                            title={'XAM'}
                            logoImageUrl={ADTE_LOGO_SMALL_URL}
                            logout={logoutObj}
                        />}

                        <Box component="main" sx={styles.content}>
                            <Box sx={styles.toolbar}/>
                            <Switch>
                                <Route component={Routes}/>{" "}
                            </Switch>
                        </Box>
                    </Box>
                    <Footer text={'XAM'} logoUrl={ADTE_LOGO_SMALL_URL}/>
                    </UserViewsContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </>
    );
};


export default connector(App);
